// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { getCountries } from 'react-phone-number-input';
import { Dropdown } from './Dropdown';

/**
 * Country picker that reuses the SAME country source as the phone input
 * (`getCountries()` from react-phone-number-input) but renders the country
 * NAME (resolved via `Intl.DisplayNames`) instead of a flag. Searchable,
 * built on top of {@link Dropdown}. The stored value is the country name
 * (e.g. "Ireland"), matching the schema's plain-string `country` field type.
 */
const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const COUNTRY_OPTIONS = getCountries()
  .map((code) => {
    const name = regionNames.of(code) ?? code;
    return { value: name, text: name };
  })
  .sort((a, b) => a.text.localeCompare(b.text));

export interface CountrySelectProps {
  label?: string;
  value?: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  fluid?: boolean;
  'data-testid'?: string;
}

export function CountrySelect({
  value,
  onChange,
  placeholder = 'Select a country',
  ...rest
}: CountrySelectProps) {
  return (
    <Dropdown
      {...rest}
      options={COUNTRY_OPTIONS}
      value={value ?? ''}
      onChange={onChange}
      search
      placeholder={placeholder}
    />
  );
}
